import { Component, ElementRef, Inject, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { RegionManagementDataService } from '@app/region-management/services/region-management-data.service';
import { Region, RegionService } from '@app/shared/services/api/regions.service';
import { AuthService } from '@app/shared/services/auth.service';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { cssVariables } from '@env/css-vars';
import { isNil } from 'lodash';
import moment from 'moment';
import { Observable, of, throwError } from 'rxjs';
import { catchError, first, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { NotificationDialogComponent } from '../../../shared/dialog/notification-dialog/notification-dialog.component';
import { MunicipalityRegionHierarchy } from '@app/shared/services/api/region-municipalities.service';

@Component({
  selector: 'app-add-edit-region-dialog',
  templateUrl: './add-edit-region-dialog.component.html',
  styleUrls: ['./add-edit-region-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AddEditRegionDialogComponent implements OnInit {
  @ViewChild('regionNameInput', ) regionNameInput: ElementRef | undefined;

  title = '';
  label = '';
  description = '';
  secondLabel = '';
  cancelText = '';
  saveText = '';
  editItem: any;
  isEdit = false;
  regionType: any;
  regionName: string = '';
  parentRegionId: string = '';
  regionHierarchyTypeIds: string[] = [];
  readonly dateFormat = 'YYYY-MM-DD[T]HH:mm:ss';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public matDialogRef: MatDialogRef<AddEditRegionDialogComponent>,
    public matDialog: MatDialog,
    private regionService: RegionService,
    private authService: AuthService,
    private overLoaderService: OverlayLoaderService,
    private regionManagementData: RegionManagementDataService
  ) {}

  ngOnInit() {
    const { title, label, description, secondLabel, cancelText, saveText, editItem, isEdit, regionType, parentRegionId, regionHierarchyTypeIds } = this.data;

    this.title = title;
    this.label = label;
    this.description = description;
    this.secondLabel = secondLabel;
    this.cancelText = cancelText;
    this.saveText = saveText;
    this.regionType = regionType;
    this.parentRegionId = parentRegionId;
    this.isEdit = isEdit;
    this.editItem = editItem;
    this.regionHierarchyTypeIds = regionHierarchyTypeIds;

    if (isEdit) {
      this.regionName = this.editItem.name;
    }
  }

  public confirm(): void {
    const name = this.regionNameInput?.nativeElement.value;

    if (isNil(name) || name === '') {
      return;
    }

    this.regionManagementData
      .findRegionNameWithParentRegionId(name, this.data.parentRegionId)
      .pipe(
        first(),
        switchMap((duplicateRegion?: Region | null) => {
          if (!!duplicateRegion) {
            return of(true);
          }

          this.overLoaderService.isLoading = true;
          return this.checkIfDuplicateName(name, this.regionHierarchyTypeIds).pipe(
            switchMap((region: Region[]) => {
              if (!!region?.length) {
                return of(true);
              }

              return this.updateOrAddRegion(name).pipe(
                tap(() => {
                  this.matDialogRef.close(true);
                  this.overLoaderService.isLoading = false;
                }),
                map(() => false)
              );
            })
          );
        }),
        catchError((err) => {
          this.matDialogRef.close(false);
          this.overLoaderService.isLoading = false;
          return throwError(() => err);
        }),
        first()
      )
      .subscribe({
        next: (hasDuplicate: boolean) => {
          if (!!hasDuplicate) {
            this.overLoaderService.isLoading = false;
            this.showNameAlreadyExistError();
          }
        },
      });
  }

  private updateOrAddRegion(name: string): Observable<Region[] | MunicipalityRegionHierarchy | undefined> {
    if (this.isEdit) {
      const editRegion = {
        name: name,
        isActive: true,
        modifyDate: moment.utc().format(this.dateFormat),
        modifyUser: this.authService.currentUser.guid,
      };

      return this.regionService.put(this.editItem.id, editRegion).pipe(
        switchMap((edittedRegion: Region) => this.updateRegion(edittedRegion)),
        catchError((err) => this.handleError(err))
      );
    }

    const regionObject = {
      name: name,
      code: null,
      description: null,
      isActive: true,
      regionTypeId: this.regionType,
      parentRegionId: this.parentRegionId,
      creationDate: moment.utc().format(this.dateFormat),
      modifyDate: moment.utc().format(this.dateFormat),
      modifyUser: this.authService.currentUser.guid,
    };

    return this.regionService.post(regionObject).pipe(
      switchMap((addedRegion: Region) => this.addRegion(addedRegion)),
      catchError((err) => this.handleError(err))
    );
  }

  showNameAlreadyExistError = () => {
    this.matDialog.open(NotificationDialogComponent, {
      data: {
        descriptionText: 'Diese Regionenbezeichnung wird bereits verwendet. Bitte verwenden Sie eine andere Bezeichnung.',
        descriptionFaClass: 'fa fa-exclamation-triangle fa-2x',
        descriptionFaColor: cssVariables['--ep-error-color'],
        hideClose: true,
      },
      panelClass: ['-md'],
    });
  };

  decline() {
    this.matDialogRef.close();
  }

  private checkIfDuplicateName(name: string, regionTypeIds: string[]): Observable<Region[]> {
    return this.regionService.getByRegionNameAndRegionType(name, regionTypeIds);
  }

  private updateRegion = (regions: Region) => of(regions).pipe(mergeMap((edittedRegion: Region) => this.regionManagementData.updateRegion(edittedRegion)));

  private addRegion = (regions: Region) => of(regions).pipe(mergeMap((addedRegion: Region) => this.regionManagementData.addRegion(addedRegion)));

  private handleError(err: any): Observable<never> {
    this.matDialog.open(NotificationDialogComponent, {
      data: {
        descriptionText: 'Bitte versuchen sie es später erneut.',
        descriptionFaClass: 'fa fa-exclamation-triangle fa-2x',
        descriptionFaColor: cssVariables['--ep-error-color'],
        hideClose: true,
      },
      panelClass: ['-md'],
    });
    return throwError(() => err);
  }
}
